import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Images, MapPin, Calendar, Package } from 'lucide-react';
import { trackPageView } from '../utils/analytics';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GalleryPage = () => {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState('All');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    trackPageView('/gallery', 'Project Gallery');
    fetch(`${BACKEND_URL}/api/gallery`).then(r => r.json()).then(setProjects).catch(() => {});
  }, []);

  const categories = ['All', ...new Set(projects.map(p => p.category))];
  const filtered = filter === 'All' ? projects : projects.filter(p => p.category === filter);

  return (
    <div className="min-h-screen bg-[#0a0a0b] pt-28 pb-20 px-4" data-testid="gallery-page">
      <SEOHead
        title="Project Gallery"
        description="View completed construction projects built with Avi Cement products — residential complexes, commercial buildings, government housing, and infrastructure."
        path="/gallery"
      />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-6xl mx-auto text-center mb-10">
        <span className="text-[#E85A1C] text-xs font-semibold tracking-[0.2em] uppercase">Project Gallery</span>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-3 mb-4">
          Humari <span className="text-[#F5A623]">Quality</span> ka Nateeja
        </h1>
        <p className="text-gray-400 text-sm max-w-md mx-auto">Lion Cement se bane successful projects — residential se infrastructure tak.</p>
      </motion.div>

      <div className="max-w-6xl mx-auto mb-8 flex gap-2 overflow-x-auto pb-2">
        {categories.map(cat => (
          <button key={cat} onClick={() => setFilter(cat)} data-testid={`gallery-filter-${cat.toLowerCase()}`}
            className={`px-5 py-2 rounded-full text-sm whitespace-nowrap transition-all ${filter === cat ? 'bg-[#E85A1C] text-white' : 'bg-[#111113] text-gray-400 border border-gray-800 hover:text-white'}`}>
            {cat}
          </button>
        ))}
      </div>

      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((project, idx) => (
          <motion.div key={project.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.08 }}
            onClick={() => setSelected(selected?.id === project.id ? null : project)}
            className="group cursor-pointer bg-[#111113] border border-gray-800 rounded-2xl overflow-hidden hover:border-[#E85A1C]/30 transition-all"
            data-testid={`gallery-project-${project.id}`}>
            <div className="aspect-video overflow-hidden">
              <img src={project.image} alt={`${project.title} - Lion Cement project`} loading="lazy" width="400" height="225"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="p-5">
              <span className="text-[#E85A1C] text-xs font-mono">{project.category}</span>
              <h3 className="text-white font-semibold mt-1 mb-2">{project.title}</h3>
              <p className="text-gray-500 text-sm mb-3 line-clamp-2">{project.description}</p>

              {selected?.id === project.id && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="border-t border-gray-800 pt-3 mt-3">
                  <div className="flex items-center gap-2 text-gray-400 text-xs mb-2">
                    <MapPin className="w-3.5 h-3.5" /> {project.location}
                  </div>
                  <div className="flex items-center gap-2 text-gray-400 text-xs mb-3">
                    <Calendar className="w-3.5 h-3.5" /> Completed: {project.completion}
                  </div>
                  <p className="text-gray-500 text-xs mb-2">Products used:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {project.products_used.map(p => (
                      <span key={p} className="px-2 py-0.5 rounded text-xs bg-[#F5A623]/10 text-[#F5A623]">{p}</span>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default GalleryPage;
